import React from 'react';

export type Screen = 'home' | 'journey' | 'key_to_success';

interface NavigationContextValue
  extends Readonly<{
    history: Screen[];
    current: Screen;
    canPop: boolean;
    push: (screen: Screen) => void;
    pop: () => void;
    reset: () => void;
  }> {}

const Context = React.createContext<NavigationContextValue>({
  history: ['home'],
  current: 'home',
  canPop: false,
  push: (screen: Screen) => {
    throw new Error('NavigationContextProvider not found!');
  },
  pop: () => {
    throw new Error('NavigationContextProvider not found!');
  },
  reset: () => {
    throw new Error('NavigationContextProvider not found!');
  },
});

export interface NavigationContextProviderProps
  extends Readonly<{
    children?: React.ReactNode;
    initialScreen: Screen;
  }> {}

export const NavigationContextProvider = React.memo<NavigationContextProviderProps>((props) => {
  const { initialScreen, children } = props;

  const [history, setHistory] = React.useState<Screen[]>(() => [initialScreen]);

  const Current = React.useMemo(() => history[history.length - 1], [history]);

  const CanPop = React.useMemo(() => history.length > 1, [history]);

  const PushCallback = React.useCallback((screen: Screen) => setHistory((prevHistory: Screen[]) => [...prevHistory, screen]), []);

  const PopCallback = React.useCallback(() => {
    if (!CanPop) {
      return;
    }

    setHistory((prevHistory: Screen[]) => {
      const updatedHistory = [...prevHistory];
      updatedHistory.pop();

      return updatedHistory;
    });
  }, [CanPop]);

  const ResetCallback = React.useCallback(() => setHistory([initialScreen]), [initialScreen]);

  const ContextValue = React.useMemo(() => {
    const contextValue: NavigationContextValue = {
      history: [...history],
      current: Current,
      canPop: CanPop,
      push: PushCallback,
      pop: PopCallback,
      reset: ResetCallback,
    };

    return contextValue;
  }, [history, Current, CanPop, PushCallback, PopCallback, ResetCallback]);

  return <Context.Provider value={ContextValue}>{children}</Context.Provider>;
});

export const useNavigationContext = () => React.useContext<NavigationContextValue>(Context);
